import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check } from "@phosphor-icons/react";
import { getDevicePlatform, openStoreLinkByDevice } from "../../utils/storeRedirect";
import { trackCreateBusinessSuccess } from "../../utils/analytics";
import "./createBusinessSuccess.css";

const WEB_APP_URL = "https://hynt.co.in/app";

const CHECKLIST_ITEMS = [
  { label: "Business category", done: true },
  { label: "Business name, year & city", done: true },
  { label: "Add portfolio photos", done: false },
  { label: "Add services & price range", done: false },
  { label: "See homeowner requirements in your city", done: false },
];

const readStoredUser = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

function CreateBusinessSuccessScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const createUserDraft = location?.state?.createUserDraft || null;

  useEffect(() => {
    trackCreateBusinessSuccess({ buttonName: "Success screen" });
  }, []);

  useEffect(() => {
    // Keep success as a terminal step: browser/device back should go home.
    window.history.pushState({ createBusinessSuccess: true }, "", window.location.href);

    const handlePopState = () => {
      navigate("/", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  const profileSummary = useMemo(() => {
    const storedUser = readStoredUser();
    const businessName = createUserDraft?.businessName || storedUser?.businessName || storedUser?.firstName || "";
    const category =
      createUserDraft?.professionalCategory ||
      createUserDraft?.category ||
      storedUser?.professionalCategory ||
      storedUser?.category ||
      "";
    const city = createUserDraft?.location || storedUser?.location || "";
    return [businessName, category, city].filter(Boolean).join(" · ");
  }, [createUserDraft]);

  const doneCount = CHECKLIST_ITEMS.filter((item) => item.done).length;
  const progressPercent = Math.round((doneCount / CHECKLIST_ITEMS.length) * 100);

  const devicePlatform = useMemo(() => getDevicePlatform(), []);
  const appHint =
    devicePlatform === "ios"
      ? "Opens App Store · you'll be signed in automatically"
      : "Opens Google Play · you'll be signed in automatically";

  const handleGetApp = () => {
    trackCreateBusinessSuccess({ buttonName: "Get the HYNT app" });
    openStoreLinkByDevice();
  };

  const handleContinueOnWebsite = () => {
    trackCreateBusinessSuccess({ buttonName: "Continue on website" });
  };

  return (
    <div className="create-business-success-page">
      <div className="create-business-success-shell">
        <main className="create-business-success-content">
          <button
            type="button"
            className="create-business-success-back-btn"
            onClick={() => navigate("/", { replace: true })}
          >
            <img src="/caretLeft.svg" alt="" />
            <span>Back to home</span>
          </button>

          <div className="create-business-success-main">
            <div className="create-business-success-check">
              <Check size={28} weight="bold" color="#ffffff" />
            </div>
            <p className="create-business-success-eyebrow">Your profile is live</p>
            <h1 >Finish your profile in the HYNT app to get noticed</h1>
            {profileSummary ? (
              <p className="create-business-success-copy">{profileSummary}.</p>
            ) : null}
            {/* <p className="create-business-success-copy">Homeowners can now discover you.</p>*/}

            <div className="create-business-success-progress-card">
              <div className="create-business-success-progress-head">
                <span>Profile {progressPercent}% complete</span>
                <span>
                  {doneCount} of {CHECKLIST_ITEMS.length}
                </span>
              </div>
              <div className="create-business-success-progress-track">
                <div
                  className="create-business-success-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <ul className="create-business-success-checklist">
                {CHECKLIST_ITEMS.map((item) => (
                  <li key={item.label} className={item.done ? "is-done" : ""}>
                    <span className="create-business-success-check-icon">
                      {item.done ? <Check size={12} weight="bold" color="#ffffff" /> : null}
                    </span>
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="create-business-success-spacer" />

          <div className="create-business-success-footer">
            <button
              type="button"
              className="create-business-success-app-btn"
              onClick={handleGetApp}
            >
              Get the HYNT app · Free
            </button>
            <p className="create-business-success-app-hint">{appHint}</p>
            <a
              href={WEB_APP_URL}
              className="create-business-success-web-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleContinueOnWebsite}
            >
              Continue on Website
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}

export default CreateBusinessSuccessScreen;
